import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserProfile } from '@dpfs/shared';

interface AuthState {
  user: UserProfile | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  requiresMfa: boolean;
  tempToken: string | null;

  // Actions
  setAuth: (user: UserProfile, accessToken: string, refreshToken: string) => void;
  setMfaRequired: (tempToken: string) => void;
  clearMfaRequired: () => void;
  updateUser: (user: Partial<UserProfile>) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      requiresMfa: false,
      tempToken: null,

      setAuth: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          requiresMfa: false,
          tempToken: null,
        }),

      setMfaRequired: (tempToken) =>
        set({
          requiresMfa: true,
          tempToken,
        }),

      clearMfaRequired: () =>
        set({
          requiresMfa: false,
          tempToken: null,
        }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          requiresMfa: false,
          tempToken: null,
        }),
    }),
    {
      name: 'dpfs-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
